/* Workers (tile grid) and Skills library */
import React from 'react';
import { Icon } from './primitives.jsx';
import { useAutoSave, SaveStatus } from '../lib/use-auto-save.jsx';
import { listWorkers, listSkills, spawnWorker, getWorker, restartWorker, deleteWorker, getSkill, createSkill, patchSkill, deleteSkill } from '../api.js';

// Sort key: lower number = higher in the list.
// Running first (action visible), then Queued (about to run),
// Failed (needs attention), Done (history).
const STATUS_RANK = { running: 0, queued: 1, failed: 2, done: 3 };

const STATUS_META = {
  running: { label: 'Running',  bg: '#E8E1FF', fg: '#5A3FA3', dot: 'var(--support-purple)' },
  queued:  { label: 'Queued',   bg: 'var(--bg-card)', fg: 'var(--fg)', dot: 'var(--fg-muted)' },
  failed:  { label: 'Failed',   bg: '#FCE6E0', fg: '#A20235', dot: '#A20235' },
  done:    { label: 'Finished', bg: '#DCF1E2', fg: '#00702A', dot: 'var(--support-green)' },
};

function timestampFor(w) {
  // Sort within a status bucket by the most relevant timestamp:
  // running → startedAt; queued → createdAt; failed/done → completedAt.
  const t = w.status === 'running' ? w.startedAt
         : w.status === 'queued'  ? w.createdAt
         : w.completedAt || w.updatedAt;
  return t ? new Date(t).getTime() : 0;
}

export const WorkersView = () => {
  const [workers, setWorkers] = React.useState(null);
  const [skills, setSkills] = React.useState(null);
  const [error, setError] = React.useState(null);
  const [spawnOpen, setSpawnOpen] = React.useState(false);
  const [detail, setDetail] = React.useState(null);
  const [filter, setFilter] = React.useState('all');
  const [restartingId, setRestartingId] = React.useState(null);

  const reload = React.useCallback(async () => {
    try {
      setError(null);
      const list = await listWorkers();
      setWorkers(list);
    } catch (err) {
      setError(err.message);
    }
  }, []);

  React.useEffect(() => {
    reload();
    listSkills().then(setSkills).catch(err => setError(err.message));
  }, [reload]);

  // Poll while anything is queued/running so the grid tracks the runner.
  React.useEffect(() => {
    if (!workers) return;
    const live = workers.some(w => w.status === 'queued' || w.status === 'running');
    if (!live) return;
    const t = setInterval(reload, 5000);
    return () => clearInterval(t);
  }, [workers, reload]);

  const sorted = React.useMemo(() => {
    if (!workers) return [];
    return [...workers].sort((a, b) => {
      const ra = STATUS_RANK[a.status] ?? 99;
      const rb = STATUS_RANK[b.status] ?? 99;
      if (ra !== rb) return ra - rb;
      return timestampFor(b) - timestampFor(a);
    });
  }, [workers]);

  const counts = React.useMemo(() => {
    const out = { all: sorted.length, running: 0, queued: 0, failed: 0, done: 0 };
    for (const w of sorted) if (out[w.status] !== undefined) out[w.status]++;
    return out;
  }, [sorted]);

  const visible = filter === 'all' ? sorted : sorted.filter(w => w.status === filter);

  const handleRestart = async w => {
    if (restartingId) return;
    setRestartingId(w._id);
    try {
      await restartWorker(w._id);
      await reload();
    } catch (err) {
      setError(err.message);
    } finally {
      setRestartingId(null);
    }
  };

  const handleDelete = async w => {
    if (!window.confirm(`Delete worker "${w.title}"? This removes the row and its result. Cannot be undone.`)) return;
    try {
      await deleteWorker(w._id);
      await reload();
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <div className="page">
      <div className="page__head">
        <h1>Workers</h1>
        <div className="lede">Long-running tasks eOS runs on your behalf. Tiles are sorted with the latest running worker at the top.</div>
      </div>

      <div style={{display:'flex',gap:10,marginBottom:16,flexWrap:'wrap',alignItems:'center'}}>
        <button className="btn-orange" onClick={() => setSpawnOpen(true)}><Icon name="plus" size={14}/> New worker</button>
        <button className="btn-ghost" onClick={reload}>Refresh</button>
        <div style={{flex:1}}/>
        <div style={{fontSize:13,color:'var(--fg-muted)',display:'flex',alignItems:'center',gap:8}}>
          <span className="live-dot"/>
          <span>{counts.running} running · {counts.queued} queued — runner ticks every minute</span>
        </div>
      </div>

      <div style={{display:'flex',gap:6,marginBottom:18,flexWrap:'wrap'}}>
        {['all', 'running', 'queued', 'failed', 'done'].map(f => (
          <span
            key={f}
            className={`tag ${filter === f ? 'tag--orange' : ''}`}
            style={{cursor:'pointer'}}
            onClick={() => setFilter(f)}
          >
            {f === 'all' ? 'All' : STATUS_META[f]?.label || f} ({counts[f]})
          </span>
        ))}
      </div>

      {error && <div style={{padding:12,marginBottom:16,background:'#FCE6E0',color:'#A20235',borderRadius:8,fontSize:13}}>{error}</div>}

      {workers === null && (
        <div style={{padding:30,textAlign:'center',color:'var(--fg-muted)',fontSize:13}}>Loading workers…</div>
      )}

      {workers !== null && visible.length === 0 && (
        <div style={{padding:24,background:'var(--bg-alt)',borderRadius:12,color:'var(--fg-muted)',fontSize:13.5,textAlign:'center'}}>
          {filter === 'all'
            ? 'No workers yet. Hit "New worker" to spawn the first one.'
            : `No ${STATUS_META[filter]?.label.toLowerCase() || filter} workers.`}
        </div>
      )}

      {workers !== null && visible.length > 0 && (
        <div className="tile-grid">
          {visible.map(w => (
            <WorkerTile
              key={w._id}
              w={w}
              restarting={restartingId === w._id}
              onOpen={() => setDetail(w._id)}
              onRestart={() => handleRestart(w)}
              onDelete={() => handleDelete(w)}
            />
          ))}
        </div>
      )}

      {spawnOpen && (
        <SpawnWorkerModal
          skills={skills}
          onClose={() => setSpawnOpen(false)}
          onSpawned={() => { setSpawnOpen(false); reload(); }}
        />
      )}
      {detail && <WorkerDetailModal id={detail} onClose={() => setDetail(null)}/>}
    </div>
  );
};

const WorkerTile = ({ w, onOpen, onRestart, onDelete, restarting }) => {
  const meta = STATUS_META[w.status] || STATUS_META.queued;
  const stamp =
    w.status === 'running' && w.startedAt   ? `started ${relativeTime(w.startedAt)}`
  : w.status === 'queued'                   ? `queued ${relativeTime(w.createdAt)}`
  : w.status === 'done'    && w.completedAt ? `finished ${relativeTime(w.completedAt)}`
  : w.status === 'failed'  && w.completedAt ? `failed ${relativeTime(w.completedAt)}`
  : '';

  return (
    <div className="kpi" style={{cursor:'pointer',position:'relative',borderLeft:`3px solid ${meta.dot}`}} onClick={onOpen}>
      <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:10}}>
        <span style={{
          fontSize:10.5,padding:'2px 8px',borderRadius:4,fontWeight:600,
          letterSpacing:'0.06em',textTransform:'uppercase',
          background: meta.bg, color: meta.fg,
        }}>{meta.label}</span>
        {w.attempts > 0 && (
          <span style={{fontSize:10.5,color:'var(--fg-muted)'}}>attempt {w.attempts}</span>
        )}
        <span style={{marginLeft:'auto',fontSize:11,color:'var(--fg-muted)'}}>{stamp}</span>
      </div>
      <div style={{fontFamily:'var(--font-display)',fontSize:17,lineHeight:1.2,marginBottom:6,paddingRight:60}}>{w.title}</div>
      <div style={{fontSize:11.5,color:'var(--fg-muted)',fontFamily:'JetBrains Mono'}}>skill: {w.skillId}</div>
      {w.status === 'failed' && w.errorMessage && (
        <div style={{fontSize:11,color:'#A20235',marginTop:6,whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>{w.errorMessage}</div>
      )}
      <div style={{position:'absolute',top:10,right:10,display:'flex',gap:6}}>
        <button
          type="button"
          title="Re-Start: spawn a new worker with the same inputs"
          onClick={e => { e.stopPropagation(); onRestart(); }}
          disabled={restarting}
          style={{
            padding:'4px 10px',fontSize:11,fontWeight:600,
            background:'var(--bg)',border:'1px solid var(--border)',borderRadius:6,
            cursor: restarting ? 'wait' : 'pointer',
            color:'var(--fg)',
          }}
        >
          {restarting ? '…' : '↻ Re-Start'}
        </button>
        <button
          type="button"
          title="Delete this worker"
          onClick={e => { e.stopPropagation(); onDelete(); }}
          style={{
            padding:'4px 8px',fontSize:11,fontWeight:600,
            background:'var(--bg)',border:'1px solid var(--border)',borderRadius:6,
            cursor:'pointer',
            color:'#A20235',
          }}
        >
          ✕
        </button>
      </div>
    </div>
  );
};

export const SpawnWorkerModal = ({ skills, onClose, onSpawned, initialSkillId = null }) => {
  const [skillId, setSkillId] = React.useState(initialSkillId);
  const [inputs, setInputs] = React.useState({});
  const [title, setTitle] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState(null);

  const skill = (skills || []).find(s => s.id === skillId);

  const updateInput = (key, value) => setInputs(prev => ({ ...prev, [key]: value }));

  const submit = async e => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await spawnWorker({ skillId, title: title || undefined, inputs });
      onSpawned();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="modal-mask" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{position:'relative',maxWidth:640}}>
        <button className="modal__close" onClick={onClose}>×</button>
        <div style={{fontSize:11,letterSpacing:'0.08em',textTransform:'uppercase',color:'var(--orange)',marginBottom:6,fontWeight:600}}>New worker</div>
        <h3>Spawn a worker.</h3>
        <p style={{color:'var(--fg-muted)',fontSize:14,marginBottom:24,marginTop:6}}>
          Pick a skill, fill the inputs, hit spawn. The runner picks it up within a minute and the output lands on this page when it's done.
        </p>

        <form onSubmit={submit}>
          <div className="field">
            <label>Skill</label>
            {skills === null ? (
              <div style={{padding:10,color:'var(--fg-muted)',fontSize:13}}>Loading skills…</div>
            ) : (
              <select className="select" value={skillId || ''} onChange={e => { setSkillId(e.target.value || null); setInputs({}); }}>
                <option value="" disabled>Choose a skill…</option>
                {skills.map(s => (
                  <option key={s.id} value={s.id}>{s.name} — {s.category}</option>
                ))}
              </select>
            )}
            {skill && <div style={{fontSize:11.5,color:'var(--fg-muted)',marginTop:6}}>{skill.description}</div>}
          </div>

          {skill && skill.inputs.map(def => (
            <div key={def.key} className="field">
              <label>{def.label}{def.required ? '' : ' (optional)'}</label>
              {def.multiline
                ? <textarea className="textarea" rows={5} placeholder={def.placeholder || ''} value={inputs[def.key] || ''} onChange={e => updateInput(def.key, e.target.value)}/>
                : <input className="input" placeholder={def.placeholder || ''} value={inputs[def.key] || ''} onChange={e => updateInput(def.key, e.target.value)}/>}
            </div>
          ))}

          {skill && (
            <div className="field">
              <label>Title (optional)</label>
              <input className="input" placeholder="Defaults to skill + first input" value={title} onChange={e => setTitle(e.target.value)}/>
            </div>
          )}

          {error && <div style={{marginBottom:14,padding:'10px 12px',background:'#FCE6E0',color:'#A20235',borderRadius:8,fontSize:13}}>{error}</div>}

          <div style={{display:'flex',gap:8,justifyContent:'flex-end',marginTop:16}}>
            <button type="button" className="btn-ghost" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn-orange" disabled={busy || !skill}>
              {busy ? 'Spawning…' : 'Spawn worker'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const WorkerDetailModal = ({ id, onClose }) => {
  const [worker, setWorker] = React.useState(null);
  const [error, setError] = React.useState(null);

  React.useEffect(() => {
    let active = true;
    const load = () => getWorker(id).then(w => { if (active) setWorker(w); }).catch(err => active && setError(err.message));
    load();
    const t = setInterval(() => {
      if (worker && (worker.status === 'done' || worker.status === 'failed')) return;
      load();
    }, 4000);
    return () => { active = false; clearInterval(t); };
  }, [id, worker?.status]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="modal-mask" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{position:'relative',maxWidth:780}}>
        <button className="modal__close" onClick={onClose}>×</button>
        {error && <div style={{padding:12,marginBottom:16,background:'#FCE6E0',color:'#A20235',borderRadius:8,fontSize:13}}>{error}</div>}
        {!worker ? (
          <div style={{padding:20,color:'var(--fg-muted)',fontSize:13}}>Loading…</div>
        ) : (
          <div>
            <div style={{fontSize:11,letterSpacing:'0.08em',textTransform:'uppercase',color:'var(--orange)',marginBottom:6,fontWeight:600}}>{worker.skillId} · {worker.status}</div>
            <h3 style={{marginBottom:6}}>{worker.title}</h3>
            <div style={{fontSize:12,color:'var(--fg-muted)',marginBottom:20}}>
              Spawned {relativeTime(worker.createdAt)}
              {worker.startedAt && <> · started {relativeTime(worker.startedAt)}</>}
              {worker.completedAt && <> · finished {relativeTime(worker.completedAt)}</>}
              {worker.attempts > 1 && <> · {worker.attempts} attempts</>}
            </div>

            <div className="section__head" style={{marginTop:0}}><h2 style={{fontSize:14}}>Inputs</h2></div>
            <div style={{padding:14,background:'var(--bg-alt)',borderRadius:8,marginBottom:18,fontSize:13,whiteSpace:'pre-wrap'}}>
              {Object.entries(worker.inputs || {}).map(([k, v]) => (
                <div key={k} style={{marginBottom:6}}>
                  <strong>{k}:</strong> {v}
                </div>
              ))}
            </div>

            {worker.status === 'failed' && worker.errorMessage && (
              <div style={{padding:14,background:'#FCE6E0',color:'#A20235',borderRadius:8,marginBottom:18,fontSize:13}}>
                {worker.errorMessage}
              </div>
            )}

            {worker.result && (
              <>
                <div className="section__head"><h2 style={{fontSize:14}}>Output</h2></div>
                <pre style={{whiteSpace:'pre-wrap',fontFamily:'inherit',fontSize:13.5,lineHeight:1.55,padding:14,background:'var(--bg-card)',borderRadius:8,maxHeight:480,overflow:'auto'}}>
                  {worker.result}
                </pre>
              </>
            )}

            {(worker.status === 'queued' || worker.status === 'running') && (
              <div style={{padding:14,background:'var(--bg-alt)',borderRadius:8,fontSize:13,color:'var(--fg-muted)'}}>
                <span className="live-dot" style={{marginRight:8}}/>
                {worker.status === 'queued' ? 'Waiting for the runner to pick this up.' : 'Running — output appears here when it finishes.'}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// All call sites here are server-stamped PAST events (spawned / started /
// finished), so future deltas — a browser clock slightly behind the
// server — are clamped instead of rendering as "in 1m".
function relativeTime(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  const abs = Math.max(0, Date.now() - d.getTime());
  const min = 60_000, hr = 60 * min, day = 24 * hr;
  const fmt = (n, unit) => `${n}${unit} ago`;
  if (abs < hr) return fmt(Math.max(1, Math.round(abs / min)), 'm');
  if (abs < day) return fmt(Math.round(abs / hr), 'h');
  return fmt(Math.round(abs / day), 'd');
}

/* ─── Skills ──────────────────────────────────────────────────────── */
const CATEGORY_COLOR = {
  Research: 'var(--support-purple)',
  Synthesis: 'var(--support-green)',
  Output: 'var(--orange)',
  Data: 'var(--support-blue, #2D70B6)',
};

export const SkillsView = ({ currentUser }) => {
  const [skills, setSkills] = React.useState(null);
  const [error, setError] = React.useState(null);
  const [spawnFor, setSpawnFor] = React.useState(null);
  const [createOpen, setCreateOpen] = React.useState(false);
  const [editing, setEditing] = React.useState(null);  // skill id

  const isAdmin = currentUser?.role === 'admin';

  const reload = React.useCallback(async () => {
    try {
      setError(null);
      const list = await listSkills();
      setSkills(list);
    } catch (err) {
      setError(err.message);
    }
  }, []);

  React.useEffect(() => { reload(); }, [reload]);

  // Auto-open the Create modal when the Roles → coverage map drops a
  // draft into sessionStorage and navigates here. The modal itself
  // consumes (and clears) the draft on init.
  React.useEffect(() => {
    try {
      if (sessionStorage.getItem(DRAFT_KEY)) setCreateOpen(true);
    } catch { /* ignore */ }
  }, []);

  const onDelete = async s => {
    if (!window.confirm(`Delete skill "${s.name}"? Removes it from the catalog and from /skills/${s.id}.md in OrgRepo.`)) return;
    try {
      await deleteSkill(s.id);
      await reload();
    } catch (err) {
      setError(err.message);
    }
  };

  const categories = skills
    ? [...new Set(skills.map(s => s.category))]
    : [];

  return (
    <div className="page">
      <div className="page__head">
        <h1>Skills</h1>
        <div className="lede">Tools eOS can call on your behalf. Pick one and spawn a worker — eOS runs it in the background and pings you when it's done. Built-in skills live in code; admin-created skills live in D1 and mirror to <code>/skills/&lt;id&gt;.md</code> in OrgRepo.</div>
      </div>

      {isAdmin && (
        <div style={{display:'flex',gap:10,marginBottom:16,flexWrap:'wrap',alignItems:'center'}}>
          <button className="btn-orange" onClick={() => setCreateOpen(true)}><Icon name="plus" size={14}/> Create skill</button>
          <button className="btn-ghost" onClick={reload}>Refresh</button>
        </div>
      )}

      {error && <div style={{padding:12,marginBottom:16,background:'#FCE6E0',color:'#A20235',borderRadius:8,fontSize:13}}>{error}</div>}

      {skills === null && !error && (
        <div style={{padding:24,color:'var(--fg-muted)',fontSize:13}}>Loading skills…</div>
      )}

      {categories.map(cat => {
        const inCat = skills.filter(s => s.category === cat);
        return (
          <div key={cat} className="section">
            <div className="section__head">
              <h2>{cat}</h2>
              <span className="lede">{inCat.length} skill{inCat.length === 1 ? '' : 's'}</span>
            </div>
            <div className="tile-grid">
              {inCat.map(s => (
                <SkillCard
                  key={s.id} s={s}
                  onSpawn={s.execMode === 'native' ? null : () => setSpawnFor(s.id)}
                  onEdit={isAdmin && !s.builtin ? () => setEditing(s.id) : null}
                  onDelete={isAdmin && !s.builtin ? () => onDelete(s) : null}
                />
              ))}
            </div>
          </div>
        );
      })}

      {skills && skills.length === 0 && !error && (
        <div style={{padding:20,color:'var(--fg-muted)',fontSize:13,background:'var(--bg-alt)',borderRadius:8}}>
          No skills in the catalog. {isAdmin ? 'Click "Create skill" above to add one.' : 'An admin needs to add skills.'}
        </div>
      )}

      {spawnFor && (
        <SpawnWorkerModal
          skills={skills}
          initialSkillId={spawnFor}
          onClose={() => setSpawnFor(null)}
          onSpawned={() => setSpawnFor(null)}
        />
      )}

      {createOpen && (
        <SkillEditorModal
          mode="create"
          onClose={() => setCreateOpen(false)}
          onSaved={async () => { setCreateOpen(false); await reload(); }}
        />
      )}

      {editing && (
        <SkillEditorModal
          key={editing}
          mode="edit"
          skillId={editing}
          onClose={async () => { setEditing(null); await reload(); }}
          onSaved={async () => { setEditing(null); await reload(); }}
        />
      )}
    </div>
  );
};

const SkillCard = ({ s, onSpawn, onEdit, onDelete }) => {
  const color = CATEGORY_COLOR[s.category] || 'var(--orange)';
  return (
    <div className="skill-card" style={{position:'relative'}}>
      <div className="skill-card__icon" style={{background: color}}>
        <Icon name={s.execMode === 'native' ? 'db' : 'sparkle'} size={20}/>
      </div>
      <div className="skill-card__title">{s.name}</div>
      <div className="skill-card__desc">{s.description}</div>
      <div style={{marginTop:10,marginBottom:10,display:'flex',gap:6,flexWrap:'wrap'}}>
        {s.builtin && (
          <span className="tag" style={{fontSize:10.5,background:'var(--bg-card)',color:'var(--fg-muted)'}}>built-in</span>
        )}
        {s.execMode === 'native' && (
          <span className="tag" style={{fontSize:10.5,background:'#E0EAF5',color:'#23507F'}}>D1 native</span>
        )}
        {s.inputs.map(i => (
          <span key={i.key} className="tag" style={{fontSize:10.5}}>
            {i.label}{i.required ? '' : ' ?'}
          </span>
        ))}
      </div>
      <div className="skill-card__foot">
        <span style={{fontSize:11,color:'var(--fg-muted)'}}>id: <code style={{fontFamily:'JetBrains Mono',fontSize:10.5,background:'var(--bg-card)',padding:'1px 5px',borderRadius:3}}>{s.id}</code></span>
        <div style={{display:'flex',gap:6}}>
          {onEdit && (
            <button className="btn-ghost" style={{padding:'4px 8px',fontSize:11}} onClick={onEdit}>
              <Icon name="edit" size={11}/> Edit
            </button>
          )}
          {onDelete && (
            <button className="btn-ghost" style={{padding:'4px 8px',fontSize:11,color:'#A20235'}} onClick={onDelete}>
              <Icon name="trash" size={11}/>
            </button>
          )}
          {onSpawn && (
            <button className="btn-orange" style={{padding:'4px 10px',fontSize:12}} onClick={onSpawn}>Spawn</button>
          )}
        </div>
      </div>
    </div>
  );
};

/* ─── SkillEditorModal: create + edit user-defined Claude skills ─── */

const DRAFT_KEY = 'erasphere.skillDraft';

/* Hand-off shape used by the Roles → coverage map "Draft skill" button
 * to pre-fill the Create Skill modal. Read once, then cleared. */
function consumeSkillDraft() {
  try {
    const raw = sessionStorage.getItem(DRAFT_KEY);
    if (!raw) return null;
    sessionStorage.removeItem(DRAFT_KEY);
    const j = JSON.parse(raw);
    if (!j || typeof j !== 'object') return null;
    const base = blankSkill();
    return {
      ...base,
      id: typeof j.id === 'string' ? j.id.toLowerCase().replace(/[^a-z0-9-]/g, '-').replace(/-+/g, '-').slice(0, 64) : '',
      name: typeof j.name === 'string' ? j.name.slice(0, 120) : '',
      category: ['Research','Synthesis','Output','Data'].includes(j.category) ? j.category : base.category,
      description: typeof j.description === 'string' ? j.description.slice(0, 600) : '',
      inputs: Array.isArray(j.inputs) && j.inputs.length ? j.inputs : base.inputs,
      systemPrompt: typeof j.systemPrompt === 'string' ? j.systemPrompt.slice(0, 4000) : '',
      userTemplate: typeof j.userTemplate === 'string' ? j.userTemplate.slice(0, 8000) : base.userTemplate,
      maxTokens: Number.isInteger(j.maxTokens) && j.maxTokens > 0 ? Math.min(8192, j.maxTokens) : base.maxTokens,
      _draftedFromGap: typeof j._draftedFromGap === 'string' ? j._draftedFromGap : null,
    };
  } catch { return null; }
}

const blankSkill = () => ({
  id: '',
  name: '',
  category: 'Output',
  description: '',
  inputs: [{ key: 'topic', label: 'Topic', required: true, multiline: false, placeholder: '' }],
  systemPrompt: '',
  userTemplate: 'Write a short brief about {{topic}}.',
  maxTokens: 1600,
});

const skillSig = f => f ? JSON.stringify({
  name: f.name.trim(),
  category: f.category,
  description: f.description.trim(),
  inputs: f.inputs,
  systemPrompt: f.systemPrompt.trim(),
  userTemplate: f.userTemplate.trim(),
  maxTokens: Number(f.maxTokens) || 1600,
}) : '';

const SkillEditorModal = ({ mode, skillId, onClose, onSaved }) => {
  const [form, setForm] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState(null);
  const [baselineSig, setBaselineSig] = React.useState(null);
  const isCreate = mode === 'create';

  React.useEffect(() => {
    if (isCreate) {
      const draft = consumeSkillDraft();
      setForm(draft || blankSkill());
      return;
    }
    getSkill(skillId)
      .then(s => {
        const next = {
          id: s.id,
          name: s.name || '',
          category: s.category || 'Output',
          description: s.description || '',
          inputs: Array.isArray(s.inputs) ? s.inputs : [],
          systemPrompt: s.systemPrompt || '',
          userTemplate: s.userTemplate || '',
          maxTokens: s.maxTokens || 1600,
        };
        setForm(next);
        setBaselineSig(skillSig(next));
      })
      .catch(err => setError(err.message));
  }, [isCreate, skillId]);

  const currentSig = skillSig(form);
  const dirty = !isCreate && !!form && baselineSig !== null && currentSig !== baselineSig;

  // Edit-mode persist for auto-save: patch in place without closing.
  const persist = async () => {
    if (!form.name.trim() || !form.description.trim() || !form.userTemplate.trim()) {
      throw new Error('Name, description and user template are required');
    }
    await patchSkill(form.id, {
      name: form.name.trim(),
      category: form.category,
      description: form.description.trim(),
      inputs: form.inputs.filter(i => i.key.trim() && i.label.trim()).map(i => ({
        key: i.key.trim(), label: i.label.trim(),
        required: !!i.required, multiline: !!i.multiline,
        placeholder: i.placeholder?.trim() || undefined,
      })),
      systemPrompt: form.systemPrompt.trim() || null,
      userTemplate: form.userTemplate.trim(),
      maxTokens: Number(form.maxTokens) || 1600,
    });
    setBaselineSig(currentSig);
  };

  const { status: saveStatus, error: saveError, saveNow } = useAutoSave({
    dirty,
    save: persist,
    enabled: !isCreate,
    signature: currentSig,
  });

  const updateInput = (idx, patch) => {
    setForm(f => ({
      ...f,
      inputs: f.inputs.map((i, k) => k === idx ? { ...i, ...patch } : i),
    }));
  };
  const addInput = () => setForm(f => ({ ...f, inputs: [...f.inputs, { key: '', label: '', required: false, multiline: false, placeholder: '' }] }));
  const removeInput = idx => setForm(f => ({ ...f, inputs: f.inputs.filter((_, k) => k !== idx) }));

  const submit = async e => {
    e.preventDefault();
    if (!isCreate) { saveNow(); return; }
    if (!form.name.trim() || !form.description.trim() || !form.userTemplate.trim()) return;
    setBusy(true); setError(null);
    try {
      const payload = {
        name: form.name.trim(),
        category: form.category,
        description: form.description.trim(),
        inputs: form.inputs.filter(i => i.key.trim() && i.label.trim()).map(i => ({
          key: i.key.trim(), label: i.label.trim(),
          required: !!i.required, multiline: !!i.multiline,
          placeholder: i.placeholder?.trim() || undefined,
        })),
        systemPrompt: form.systemPrompt.trim() || null,
        userTemplate: form.userTemplate.trim(),
        maxTokens: Number(form.maxTokens) || 1600,
      };
      if (form.id.trim()) payload.id = form.id.trim();
      await createSkill(payload);
      onSaved?.();
    } catch (err) {
      setError(err.message);
      setBusy(false);
    }
  };

  if (!form && !error) {
    return (
      <div className="modal-mask" onClick={onClose}>
        <div className="modal" onClick={e => e.stopPropagation()}>
          <button className="modal__close" onClick={onClose}>×</button>
          <div style={{padding:24,color:'var(--fg-muted)',fontSize:13}}>Loading skill…</div>
        </div>
      </div>
    );
  }

  return (
    <div className="modal-mask" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{position:'relative',maxWidth:880,width:'96%'}}>
        <button className="modal__close" onClick={onClose}>×</button>
        <div style={{fontSize:11,letterSpacing:'0.08em',textTransform:'uppercase',color:'var(--orange)',marginBottom:6,fontWeight:600}}>{isCreate ? 'Create skill' : 'Edit skill'}</div>
        <h3 style={{marginBottom:6}}>{isCreate ? 'New Claude skill' : `Editing: ${form?.name}`}</h3>
        <p style={{color:'var(--fg-muted)',fontSize:12.5,marginBottom:14,lineHeight:1.5,maxWidth:'72ch'}}>
          User-defined skills are Claude prompt templates. Reference inputs in the user template with <code>{'{{key}}'}</code>. They land in <code>/skills/{form?.id || '<id>'}.md</code> in OrgRepo.
        </p>

        {isCreate && form?._draftedFromGap && (
          <div style={{marginBottom:14,padding:'8px 12px',background:'#FFE7DC',color:'#B83C0F',borderRadius:8,fontSize:12.5,display:'flex',alignItems:'center',gap:8}}>
            <span style={{fontWeight:600}}>Drafted from coverage gap:</span>
            <span style={{flex:1}}>{form._draftedFromGap}</span>
          </div>
        )}

        {error && <div style={{margin:'0 0 14px',padding:10,background:'#FCE6E0',color:'#A20235',borderRadius:8,fontSize:13}}>{error}</div>}

        {form && (
          <form onSubmit={submit} className="intake-form">
            <div className="intake-cols">
              <div className="intake-col">
                <div className="field respo-2col" style={{gap:12,margin:0}}>
                  <div>
                    <label>Name</label>
                    <input className="input" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} maxLength={120}/>
                  </div>
                  <div>
                    <label>Category</label>
                    <select className="select" value={form.category} onChange={e => setForm({ ...form, category: e.target.value })}>
                      <option>Research</option>
                      <option>Synthesis</option>
                      <option>Output</option>
                      <option>Data</option>
                    </select>
                  </div>
                </div>

                {isCreate && (
                  <div className="field" style={{margin:0}}>
                    <label>Slug (id)</label>
                    <input
                      className="input"
                      placeholder="auto-derived from name if blank"
                      value={form.id}
                      onChange={e => setForm({ ...form, id: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-').replace(/-+/g, '-') })}
                      maxLength={64}
                    />
                    <div style={{fontSize:11,color:'var(--fg-muted)',marginTop:4}}>lowercase letters, digits, hyphens. Used as the URL slug + OrgRepo path.</div>
                  </div>
                )}

                <div className="field" style={{margin:0}}>
                  <label>Description</label>
                  <textarea className="textarea" rows={2} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} maxLength={600}/>
                  <div style={{fontSize:11,color:'var(--fg-muted)',marginTop:4}}>Shown to humans AND to the planner — make it clear when to pick this skill.</div>
                </div>

                <div className="field" style={{maxWidth:160,margin:0}}>
                  <label>Max tokens</label>
                  <input
                    className="input"
                    type="number"
                    min={256}
                    max={8192}
                    value={form.maxTokens}
                    onChange={e => setForm({ ...form, maxTokens: e.target.value })}
                  />
                </div>
              </div>

              <div className="intake-col">
                <div className="field" style={{margin:0}}>
                  <label>System prompt (optional)</label>
                  <textarea className="textarea" rows={3} value={form.systemPrompt} onChange={e => setForm({ ...form, systemPrompt: e.target.value })} maxLength={4000}/>
                </div>

                <div className="field" style={{margin:0}}>
                  <label>User template (Claude prompt body)</label>
                  <textarea
                    className="textarea"
                    rows={8}
                    value={form.userTemplate}
                    onChange={e => setForm({ ...form, userTemplate: e.target.value })}
                    maxLength={8000}
                    style={{fontFamily:'JetBrains Mono',fontSize:12.5}}
                  />
                  <div style={{fontSize:11,color:'var(--fg-muted)',marginTop:4}}>Use <code>{'{{key}}'}</code> placeholders for inputs. {form.userTemplate.length} / 8000</div>
                </div>
              </div>

              <div className="field intake-span" style={{margin:0}}>
                <label>Inputs</label>
                <div style={{display:'flex',flexDirection:'column',gap:8}}>
                  {form.inputs.map((inp, idx) => (
                    <div key={idx} className="skill-input-row">
                      <input
                        className="input"
                        placeholder="key"
                        value={inp.key}
                        onChange={e => updateInput(idx, { key: e.target.value.replace(/[^a-zA-Z0-9_-]/g, '') })}
                        style={{fontFamily:'JetBrains Mono',fontSize:12}}
                      />
                      <input
                        className="input"
                        placeholder="label"
                        value={inp.label}
                        onChange={e => updateInput(idx, { label: e.target.value })}
                      />
                      <label style={{fontSize:11,display:'flex',gap:4,alignItems:'center'}}>
                        <input type="checkbox" checked={!!inp.required} onChange={e => updateInput(idx, { required: e.target.checked })}/>
                        required
                      </label>
                      <label style={{fontSize:11,display:'flex',gap:4,alignItems:'center'}}>
                        <input type="checkbox" checked={!!inp.multiline} onChange={e => updateInput(idx, { multiline: e.target.checked })}/>
                        multiline
                      </label>
                      <button type="button" className="btn-ghost" style={{padding:'2px 6px',fontSize:13,color:'#A20235'}} onClick={() => removeInput(idx)}>×</button>
                    </div>
                  ))}
                  <button type="button" className="btn-ghost" style={{alignSelf:'flex-start',padding:'4px 10px',fontSize:11.5}} onClick={addInput}>+ Add input</button>
                </div>
              </div>
            </div>

            <div style={{display:'flex',gap:8,justifyContent:'flex-end',alignItems:'center'}}>
              {isCreate ? (
                <>
                  <button type="button" className="btn-ghost" onClick={onClose}>Cancel</button>
                  <button type="submit" className="btn-orange" disabled={busy || !form.name.trim() || !form.description.trim() || !form.userTemplate.trim()}>
                    {busy ? 'Saving…' : 'Create'}
                  </button>
                </>
              ) : (
                <>
                  <SaveStatus status={saveStatus} error={saveError} dirty={dirty}/>
                  <button type="button" className="btn-orange" onClick={() => { saveNow(); onClose(); }}>Done</button>
                </>
              )}
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

